import { useEffect, useRef, useState, useCallback } from "react";
import { geoEqualEarth, geoPath, geoGraticule10 } from "d3-geo";
import { loadCountries, codeOf, NON_COUNTRY, MapPlace } from "./WorldMap";
import { loadUkSubunits } from "./ukSubunits";

const VISITED = "#3aa76d";
const VISITED_LINE = "#2e8c5a";
const UNVISITED_FILL = "#fbfaf6";
const UNVISITED_LINE = "rgba(26,26,26,0.22)";

// A flat world map drawn on canvas with the Equal Earth projection (equal-area,
// so country sizes are honest — unlike the Mercator "Karta"). Visited countries
// fill green; saved places show as gold dots you can hover/tap to see the year.
export const EqualEarthMap = ({
  visited,
  places = [],
}: {
  visited: string[];
  places?: MapPlace[];
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dataRef = useRef<{ countries: any[]; uk: any[] } | null>(null);
  const visitedRef = useRef<Set<string>>(new Set(visited));
  const placesRef = useRef<MapPlace[]>(places);
  const sizeRef = useRef({ w: 0, h: 0 });
  const projRef = useRef<any>(null);
  const [tip, setTip] = useState<{ name: string; x: number; y: number } | null>(null);

  visitedRef.current = new Set(visited);
  placesRef.current = places;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const data = dataRef.current;
    if (!canvas || !data) return;
    const { w, h } = sizeRef.current;
    if (w === 0 || h === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    const projection = geoEqualEarth().fitExtent([[6, 6], [w - 6, h - 6]], { type: "Sphere" } as any);
    projRef.current = projection;
    const path = geoPath(projection, ctx as any);

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    // ocean
    ctx.beginPath();
    path({ type: "Sphere" } as any);
    ctx.fillStyle = "#e3eaec";
    ctx.fill();

    // graticule
    ctx.beginPath();
    path(geoGraticule10() as any);
    ctx.strokeStyle = "rgba(26,26,26,0.06)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    const drawFeat = (f: any, code: string) => {
      const on = visitedRef.current.has(code);
      ctx.beginPath();
      path(f);
      ctx.fillStyle = on ? VISITED : UNVISITED_FILL;
      ctx.fill();
      ctx.strokeStyle = on ? VISITED_LINE : UNVISITED_LINE;
      ctx.lineWidth = on ? 0.7 : 0.4;
      ctx.stroke();
    };
    data.countries.forEach((f) => drawFeat(f, codeOf(f.properties)));
    data.uk.forEach((f) => drawFeat(f, f.properties.code));

    // sphere outline
    ctx.beginPath();
    path({ type: "Sphere" } as any);
    ctx.strokeStyle = "rgba(26,26,26,0.3)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // place dots
    placesRef.current.forEach((p) => {
      const xy = projection([p.lng, p.lat]);
      if (!xy) return;
      ctx.beginPath();
      ctx.arc(xy[0], xy[1], 4, 0, 2 * Math.PI);
      ctx.fillStyle = "#e2b31a";
      ctx.fill();
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    ctx.restore();
  }, []);

  // load geometry once
  useEffect(() => {
    let cancel = false;
    Promise.all([loadCountries(), loadUkSubunits().catch(() => null)]).then(([data, uk]) => {
      if (cancel) return;
      dataRef.current = {
        countries: data.features.filter(
          (f: any) => codeOf(f.properties) !== "GB" && !NON_COUNTRY.has(f.properties.CONTINENT)
        ),
        uk: uk ? uk.features : [],
      };
      draw();
    });
    return () => { cancel = true; };
  }, [draw]);

  // keep the canvas sized to its container (hi-dpi) and redraw
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const resize = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w, h };
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      draw();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  // redraw when the visited set / places change
  useEffect(() => { draw(); }, [visited, places, draw]);

  // nearest place under the pointer (for the hover/tap label)
  const hitTest = (e: React.PointerEvent | React.MouseEvent): boolean => {
    const canvas = canvasRef.current;
    const projection = projRef.current;
    if (!canvas || !projection) return false;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let found: { name: string; x: number; y: number } | null = null;
    for (const p of placesRef.current) {
      const xy = projection([p.lng, p.lat]);
      if (!xy) continue;
      if ((xy[0] - mx) ** 2 + (xy[1] - my) ** 2 <= 169) {
        found = { name: p.date ? `${p.name} · ${p.date}` : p.name, x: xy[0], y: xy[1] };
        break;
      }
    }
    setTip(found);
    return found !== null;
  };

  return (
    <div className="places__flatmap" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="places__flatmap-canvas"
        onMouseMove={(e) => { if (e.nativeEvent instanceof MouseEvent) hitTest(e); }}
        onMouseLeave={() => setTip(null)}
        onPointerDown={(e) => { if (e.pointerType !== "mouse") hitTest(e); }}
      />
      {tip && (
        <div className="places__globe-tip" style={{ left: tip.x, top: tip.y }}>
          {tip.name}
        </div>
      )}
    </div>
  );
};
